const initializedSlots = new Set<string>();

/**
 * Adds a `<link rel="preload">` for a script URL so the browser can start fetching it
 * before a related script tag is injected (e.g. preloading LiveRamp's launchpad bundle
 * ahead of the launchpad-liveramp wrapper script).
 */
export function injectPreloadLink(href: string): void {
    if (typeof document === 'undefined') {
        return;
    }

    for (const link of Array.from(document.querySelectorAll('link[rel="preload"]'))) {
        if (link.getAttribute('href') === href) {
            return;
        }
    }

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = href;
    document.head.append(link);
}

type InjectScriptOptions = {
    defer?: boolean;
    attributes?: Record<string, string>;
};

type ScriptWithComplete = HTMLScriptElement & { complete?: boolean; readyState?: string };

function getScriptBySrc(src: string): HTMLScriptElement | undefined {
    return Array.from(document.scripts).find(script => script.src === src);
}

function isScriptLoadComplete(script: HTMLScriptElement): boolean {
    if (script.dataset.adLoaded === '1') {
        return true;
    }

    const scriptWithComplete = script as ScriptWithComplete;
    if (scriptWithComplete.complete === true) {
        return true;
    }

    return scriptWithComplete.readyState === 'complete' || scriptWithComplete.readyState === 'loaded';
}

function waitForScriptLoad(script: HTMLScriptElement): Promise<HTMLScriptElement> {
    if (isScriptLoadComplete(script)) {
        script.dataset.adLoaded = '1';
        return Promise.resolve(script);
    }

    return new Promise<HTMLScriptElement>((resolve, reject) => {
        const onLoad = (): void => {
            script.dataset.adLoaded = '1';
            resolve(script);
        };
        const onError = (event: Event): void => {
            reject((event as ErrorEvent).error ?? new Error('Script load failed'));
        };

        script.addEventListener('load', onLoad, { once: true });
        script.addEventListener('error', onError, { once: true });

        if (isScriptLoadComplete(script)) {
            script.removeEventListener('load', onLoad);
            script.removeEventListener('error', onError);
            onLoad();
        }
    });
}

/**
 * Waits for an existing script tag or injects one if missing. Handles SSR-injected
 * scripts that may still be loading when client code runs.
 */
export function ensureScriptLoaded(src: string, options: InjectScriptOptions = {}): Promise<HTMLScriptElement> {
    if (typeof document === 'undefined') {
        return Promise.reject(new Error('document is unavailable'));
    }

    const existing = getScriptBySrc(src);
    if (existing) {
        return waitForScriptLoad(existing);
    }

    return injectScriptOnce(src, options).then(script => {
        script.dataset.adLoaded = '1';
        return script;
    });
}

/**
 * Injects an external script tag once per URL and returns when it has loaded.
 * Multiple ad slots or components can call this with the same URL; concurrent callers
 * share the in-flight or completed load instead of duplicating tags.
 */
export function injectScriptOnce(src: string, options: InjectScriptOptions = {}): Promise<HTMLScriptElement> {
    if (typeof document === 'undefined') {
        return Promise.reject(new Error('document is unavailable'));
    }

    for (const script of Array.from(document.scripts)) {
        if (script.src === src) {
            if (script.dataset.loading === '1') {
                return new Promise<HTMLScriptElement>((resolve, reject) => {
                    script.addEventListener('load', () => resolve(script), { once: true });
                    script.addEventListener('error', event => reject(event.error ?? new Error('Script load failed')), {
                        once: true,
                    });
                });
            }

            return Promise.resolve(script);
        }
    }

    return new Promise<HTMLScriptElement>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.dataset.loading = '1';

        if (options.defer) {
            script.defer = true;
        }

        if (options.attributes) {
            for (const [name, value] of Object.entries(options.attributes)) {
                script.setAttribute(name, value);
            }
        }

        script.addEventListener(
            'load',
            () => {
                delete script.dataset.loading;
                resolve(script);
            },
            { once: true },
        );
        script.addEventListener(
            'error',
            event => {
                delete script.dataset.loading;
                script.remove();
                reject(event.error ?? new Error(`Failed to load script: ${src}`));
            },
            { once: true },
        );

        const existingScript = document.querySelector('script');
        if (existingScript) {
            existingScript.before(script);
        } else {
            document.head.append(script);
        }
    });
}

/**
 * Marks a slot div as initialized so provider setup (GPT listeners, refresh calls, etc.)
 * runs only once per divId — guards against duplicate init from React remounts or effect re-runs.
 */
export function claimSlotInit(divId: string): boolean {
    if (initializedSlots.has(divId)) {
        return false;
    }

    initializedSlots.add(divId);
    return true;
}

/** Clears a slot's init claim on unmount so the same divId can initialize again after remount. */
export function releaseSlotInit(divId: string): void {
    initializedSlots.delete(divId);
}
